import { MenuProps } from "antd";
import { AdminModuleKey } from "../config/adminModules";

type MenuItem = Required<MenuProps>["items"][number];
type NonNullMenuItem = Exclude<MenuItem, null>;

type MenuItemNode = NonNullMenuItem & {
  key?: string | number;
  children?: MenuItem[];
};

const isMenuItem = (
  item: MenuItem | null | undefined
): item is NonNullMenuItem =>
  item != null && typeof item === "object" && "key" in item;

export function parseAllowedModules(
  value: unknown
): AdminModuleKey[] | null {
  if (value == null) return null;
  if (Array.isArray(value)) {
    return value.filter((k): k is string => typeof k === "string");
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Filtra ítems del menú según claves permitidas.
 * Si `allowedKeys` está vacío o es null, devuelve el menú completo.
 */
export function filterMenuByModuleKeys(
  items: MenuItem[],
  allowedKeys: AdminModuleKey[] | null | undefined
): MenuItem[] {
  if (!allowedKeys?.length) return items;

  const allowed = new Set(allowedKeys);

  const filterRecursive = (menuItems: MenuItem[]): MenuItem[] => {
    const result: MenuItem[] = [];

    for (const raw of menuItems) {
      if (!isMenuItem(raw)) continue;

      const node = raw as MenuItemNode;
      const key = String(node.key ?? "");
      const children = node.children;

      if (children?.length) {
        const filteredChildren = filterRecursive(children);
        if (filteredChildren.length > 0) {
          result.push({ ...node, children: filteredChildren } as MenuItem);
        }
        continue;
      }

      if (allowed.has(key)) {
        result.push(node as MenuItem);
      }
    }

    return result;
  };

  return filterRecursive(items);
}
